import { useState } from "react";
import { motion } from "framer-motion";
import { Send, Phone, Mail, MapPin, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceTypes = [
  { value: "custom_clothing", label: "Custom Clothing" },
  { value: "prom_dress", label: "Prom Dress" },
  { value: "custom_cap", label: "Custom Cap" },
  { value: "custom_shoes", label: "Custom Shoes" },
  { value: "girls_night", label: "Girls Night Event" },
  { value: "private_party", label: "Private Party" },
  { value: "general", label: "General Inquiry" },
];

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service_type: "general",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/inquiries`, formData);
      toast.success("Message sent successfully! Daina will be in touch soon.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        service_type: "general",
        message: "",
      });
    } catch (error) {
      console.error("Error submitting inquiry:", error);
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 md:py-32 px-4 md:px-8 lg:px-16 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Mail className="w-8 h-8 text-rose mb-6" />
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl mb-4">
              Get In <span className="text-rose">Touch</span>
            </h2>
            <p className="text-zinc-400 text-lg leading-relaxed mb-12">
              Have questions about our services or want to discuss a custom project? 
              Reach out and let&apos;s create something beautiful together.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-rose/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-rose" />
                </div>
                <div>
                  <p className="font-medium text-white mb-1">Call or Text</p>
                  <a 
                    href="tel:7085390789" 
                    className="text-zinc-400 hover:text-rose transition-colors"
                    data-testid="phone-link"
                  >
                    708-539-0789
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-rose/10 flex items-center justify-center flex-shrink-0">
                  <i className="fab fa-instagram text-rose text-xl" />
                </div>
                <div>
                  <p className="font-medium text-white mb-1">Instagram</p>
                  <a 
                    href="https://www.instagram.com/dainalaneebrand/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-zinc-400 hover:text-rose transition-colors"
                    data-testid="instagram-contact-link"
                  >
                    @dainalaneebrand
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-rose/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-rose" />
                </div>
                <div>
                  <p className="font-medium text-white mb-1">Based In</p>
                  <p className="text-zinc-400">Chicago Area</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your Name *"
                  required
                  className="fashion-input"
                  data-testid="contact-name-input"
                />
              </div>

              <div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Your Email *"
                  required
                  className="fashion-input"
                  data-testid="contact-email-input"
                />
              </div>

              <div>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Your Phone (Optional)"
                  className="fashion-input"
                  data-testid="contact-phone-input"
                />
              </div>

              <div>
                <select
                  name="service_type"
                  value={formData.service_type}
                  onChange={handleChange}
                  className="fashion-input cursor-pointer bg-transparent"
                  data-testid="contact-service-select"
                >
                  {serviceTypes.map((type) => (
                    <option 
                      key={type.value} 
                      value={type.value}
                      className="bg-[#0A0A0A] text-white"
                    >
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your project or event *"
                  required
                  rows={5}
                  className="fashion-input resize-none"
                  data-testid="contact-message-input"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full flex items-center justify-center gap-3"
                data-testid="contact-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
